import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useDistanceUnit } from "../contexts/DistanceUnitContext";
import { API_BASE_URL } from "../config";
import ActivityTable from "./ActivityTable";
import { parseActivityDate } from "../utils/dateUtils";

const LongestStreak = () => {
  const [streakData, setStreakData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const { unit } = useDistanceUnit();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        // Fetch streak data
        const streakResponse = await axios.get(`${API_BASE_URL}/runstreak`, {
          params: { email: user.email },
          withCredentials: true
        });
        setStreakData(streakResponse.data);

        // Fetch activities
        const activitiesResponse = await axios.get(`${API_BASE_URL}/activities`, {
          params: { email: user.email },
          withCredentials: true
        });

        // Filter activities for longest streak period
        if (streakResponse.data.longest_streak_start && streakResponse.data.longest_streak_end) {
          const startDate = new Date(streakResponse.data.longest_streak_start);
          const endDate = new Date(streakResponse.data.longest_streak_end);
          endDate.setHours(23, 59, 59, 999); // Include entire end day

          const streakActivities = activitiesResponse.data.activities.filter(activity => {
            const activityDate = parseActivityDate(activity);
            return activityDate && activityDate >= startDate && activityDate <= endDate;
          });

          setActivities(streakActivities);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="container"><div className="card"><p>Loading...</p></div></div>;
  }

  if (!streakData || streakData.longest_streak === 0) {
    return (
      <div className="container">
        <div className="card">
          <h1>🏆 Longest Streak</h1>
          <p>You don't have any streak records yet. Start running to build your longest streak!</p>
          <Link to="/dashboard">← Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const convertMilesToUnit = (miles) => {
    if (!miles) return 0;
    if (unit === 'km') {
      return (miles * 1.60934).toFixed(2);
    }
    return miles;
  };

  const formatStreakDuration = (days) => {
    const years = Math.floor(days / 365);
    const remainingAfterYears = days % 365;
    const months = Math.floor(remainingAfterYears / 30);
    const remainingDays = remainingAfterYears % 30;

    const parts = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
    if (months > 0) parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
    if (remainingDays > 0 || parts.length === 0) parts.push(`${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`);

    return parts.join(', ');
  };

  return (
    <div className="container">
      <div className="card">
        <Link to="/dashboard" style={{ textDecoration: 'none', color: '#14b8a6', marginBottom: '20px', display: 'inline-block' }}>
          ← Back to Dashboard
        </Link>
        <h1>🏆 Longest Streak</h1>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', margin: '20px 0' }}>
          <div style={{ textAlign: 'center', padding: '20px', background: '#fffbeb', borderRadius: '12px' }}>
            <h3 style={{ color: '#f59e0b', fontSize: '48px', margin: '0' }}>{streakData.longest_streak}</h3>
            <p style={{ color: '#6b7280', margin: '8px 0' }}>Days</p>
            <p style={{ color: '#f59e0b', fontSize: '14px', fontWeight: '600', marginTop: '8px' }}>
              {formatStreakDuration(streakData.longest_streak)}
            </p>
          </div>
          <div style={{ textAlign: 'center', padding: '20px', background: '#fffbeb', borderRadius: '12px' }}>
            <h3 style={{ color: '#f59e0b', fontSize: '48px', margin: '0' }}>{convertMilesToUnit(streakData.longest_streak_distance)}</h3>
            <p style={{ color: '#6b7280', margin: '8px 0' }}>{unit === 'km' ? 'Kilometers' : 'Miles'}</p>
          </div>
        </div>

        <div style={{ margin: '20px 0', padding: '20px', background: '#f9fafb', borderRadius: '12px' }}>
          <h3>Streak Period</h3>
          <p>📅 {formatDate(streakData.longest_streak_start)} - {formatDate(streakData.longest_streak_end)}</p>
          <p style={{ color: '#f59e0b', fontWeight: '600', marginTop: '12px' }}>🏆 Personal Best!</p>
        </div>
      </div>

      <div className="card">
        <h2>Activities in Longest Streak ({activities.length})</h2>
        <ActivityTable activities={activities} showDateFilter={false} />
      </div>
    </div>
  );
};

export default LongestStreak;
